<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />
        <translator
            v-if="baseDocument"
            :base-document="baseDocument"
            :document-type="documentType"
            :language="form.language"
        ></translator>

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            name="name"
            label="* Name"
            v-model="form.name"
            lpignore
            :error="errors.name ? errors.name[0] : null"
            :maxlength=200
        />

        <media-reference
            :language="form.language"
            v-model="form.photo"
            class="mt-3"
            label="Logo"
            :media-types="['OperatorLogo']"
            name="photo_id"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Company Behind</p>
            <reference
                create-text="Create new company behind and link company behind to this page"
                link-text="Link an existing company behind"
                v-model="form.company_behind"
                model-data="Company Behind"
                :should-open-new-window="true"
                :creatable="false"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            v-if="data"
        >
            <p class="text-gray-700 mb-4">Bonuses</p>
            <multi-reference
                create-text="Create new bonus and link bonus to this page"
                link-text="Link existing bonuses"
                :linkable="false"
                :disabled="form.is_out_of_business == 1 || form.is_not_recommended"
                :language="form.language"
                v-model="form.bonuses"
                field-name="bonuses"
                model-data="Bonus"
                :should-open-new-window="true"
                :pre-filled="{
                    casino: this.cleanData(data) || null
                }"
                :highlight="bonusHighlight"
            />
        </div>

        <input-date
            label="Launch Date"
            name="launch_date"
            v-model="form.launch_date"
            class="mt-2 max-w-4xl mt-3"
            :error="errors.launch_date ? errors.launch_date[0] : null"
        />
        <Select
            label="Out of business?"
            name="is_out_of_business"
            v-model="form.is_out_of_business"
            class="mt-2"
            :options="[
                {value: 0, name: 'No', default: true},
                {value: 1, name: 'Yes'},
            ]"
            :error="errors.is_out_of_business ? errors.is_out_of_business[0] : null"
        />

        <input-date
            v-if="form.is_out_of_business == 1"
            label="Out of business date"
            name="out_of_business_date"
            v-model="form.out_of_business_date"
            class="mt-2"
            :error="errors.out_of_business_date ? errors.out_of_business_date[0] : null"
        />

        <input-checkbox
            name="is_not_recommended"
            label="Not Recommended? (check if not recommended)"
            class="mt-3"
            v-model="form.is_not_recommended"
            :error="errors.is_not_recommended ? errors.is_not_recommended[0] : null"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Devices</p>
            <multi-reference
                create-text="Create new device and link device to this page"
                link-text="Link existing devices"
                :creatable="false"
                :two-step-create="true"
                :language="form.language"
                v-model="form.devices"
                field-name="devices"
                model-data="Device"
                :should-open-new-window="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Licenses</p>
            <multi-reference
                create-text="Create new license and link license to this page"
                link-text="Link existing licenses"
                :creatable="false"
                v-model="form.licenses"
                :two-step-create="true"
                field-name="licenses"
                model-data="License"
                :should-open-new-window="true"
                :language="form.language"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Target Markets</p>
            <multi-reference
                create-text="Create new target market and link target market to this page"
                link-text="Link existing target markets"
                :creatable="false"
                :language="form.language"
                v-model="form.targetMarkets"
                field-name="targetMarkets"
                :excluded-ids="form.restrictedCountries ? form.restrictedCountries.map(e => e.id) : []"
                model-data="Country"
                :should-open-new-window="true"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Restricted Countries</p>
            <multi-reference
                create-text="Create new restricted country and link restricted country to this page"
                link-text="Link existing restricted countries"
                :creatable="false"
                :language="form.language"
                v-model="form.restrictedCountries"
                :excluded-ids="form.targetMarkets ? form.targetMarkets.map(e => e.id) : []"
                field-name="restrictedCountries"
                model-data="Country"
                :should-open-new-window="true"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Game Types</p>
            <multi-reference
                create-text="Create new game type and link game type to this page"
                link-text="Link existing game types"
                :language="form.language"
                v-model="form.gameTypes"
                field-name="gameTypes"
                model-data="Game Type"
                :should-open-new-window="true"
                :show-select-all="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Deposit Methods</p>
            <multi-reference
                create-text="Create new deposit method and link deposit method to this page"
                link-text="Link existing deposit methods"
                :creatable="false"
                :language="form.language"
                v-model="form.depositMethods"
                field-name="depositMethods"
                model-data="Payment Method"
                :should-open-new-window="true"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Withdrawal Methods</p>
            <multi-reference
                create-text="Create new withdrawal method and link withdrawal method to this page"
                link-text="Link existing withdrawal methods"
                :creatable="false"
                :language="form.language"
                v-model="form.withdrawalMethods"
                field-name="withdrawalMethods"
                model-data="Payment Method"
                :should-open-new-window="true"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Currencies</p>
            <multi-reference
                create-text="Create new currency and link currency to this page"
                link-text="Link existing currencies"
                :creatable="false"
                :language="form.language"
                v-model="form.currencies"
                model-data="Currency"
                :should-open-new-window="true"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Languages</p>
            <multi-reference
                create-text="Create new language and link language to this page"
                link-text="Link existing languages"
                :creatable="false"
                :language="form.language"
                v-model="form.casinoLanguages"
                field-name="casinoLanguages"
                model-data="Casino Language"
                :should-open-new-window="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>


        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Trust checkers</p>
            <multi-reference
                create-text="Create new trust checker and link trust checker to this page"
                link-text="Link existing trust checkers"
                :creatable="false"
                :language="form.language"
                v-model="form.trust_checkers"
                :should-open-new-window="true"
                model-data="Trust Checker"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Software</p>
            <multi-reference
                :draggable="false"
                create-text="Create new software and link software to this page"
                link-text="Link existing softwares"
                :language="form.language"
                v-model="form.software"
                model-data="Software"
                :should-open-new-window="true"
                :full-width="true"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <Select
            label="Mobile site experience"
            name="mobile_site_experience"
            v-model="form.mobile_site_experience"
            class="mt-2"
            :options="[
                {value: null, name: ''},
                {value: 1, name: 1},
                {value: 2, name: 2},
                {value: 3, name: 3},
                {value: 4, name: 4},
                {value: 5, name: 5},
            ]"
            :error="errors.mobile_site_experience ? errors.mobile_site_experience[0] : null"
        />

        <Select
            label="Available as mobile app?"
            name="available_as_mobile_app"
            v-model="form.available_as_mobile_app"
            class="mt-2"
            :options="[
                {value: 0, name: 'No'},
                {value: 1, name: 'Yes'},
            ]"
            :error="errors.available_as_mobile_app ? errors.available_as_mobile_app[0] : null"
        />
        <Select
            label="Has live chat?"
            name="has_live_chat"
            v-model="form.has_live_chat"
            class="mt-2"
            :options="[
                {value: 0, name: 'No'},
                {value: 1, name: 'Yes'},
            ]"
            :error="errors.has_live_chat ? errors.has_live_chat[0] : null"
        />

       <Select
            label="Is compliant?"
            name="is_compliant"
            v-model="form.is_compliant"
            class="mt-2"
            :options="[
                {value: '', name: ''},
                {value: 0, name: 'No'},
                {value: 1, name: 'Yes'},
            ]"
            :error="errors.is_compliant ? errors.is_compliant[0] : null"
        />

        <Select
            label="Payout within (mins)"
            name="payout_within_mins"
            v-model="form.payout_within_mins"
            class="mt-2"
            :options="[
                {value: 0, name: '0 mins (Instant)'},
                {value: 15, name: '15 mins'},
                {value: 30, name: '30 mins'},
                {value: 45, name: '45 mins'},
                {value: 60, name: '1 hr'},
                {value: 90, name: '1 hr 30 mins'},
                {value: 120, name: '2 hrs'},
                {value: 180, name: '3 hrs'},
                {value: 300, name: '5 hrs'},
                {value: 1440, name: '24 hrs'},
                {value: 2880, name: '48 hrs'},
                {value: 4320, name: '+72 hrs'},
            ]"
            :error="errors.payout_within_mins ? errors.payout_within_mins[0] : null"
        />

        <input-text
            label="Payout Disclaimer"
            name="payout_disclaimer"
            class="mt-3"
            v-model="form.payout_disclaimer"
            :error="errors.payout_disclaimer ? errors.payout_disclaimer[0] : null"
            :maxlength=255
        />

        <Select
            label="Deposit within (mins)"
            name="deposit_time"
            v-model="form.deposit_time"
            class="mt-2"
            :options="[
                {value: 0, name: '0 mins (Instant)'},
                {value: 1, name: '1 min'},
                {value: 15, name: '15 mins'},
                {value: 30, name: '30 mins'},
                {value: 45, name: '45 mins'},
                {value: 60, name: '1 hr'},
                {value: 90, name: '1 hr 30 mins'},
                {value: 120, name: '2 hrs'},
                {value: 180, name: '3 hrs'},
                {value: 300, name: '5 hrs'},
                {value: 1440, name: '24 hrs'},
                {value: 2880, name: '48 hrs'},
                {value: 4320, name: '+72 hrs'},
            ]"
            :error="errors.deposit_time ? errors.deposit_time[0] : null"
        />

        <input-text
            label="Deposit Disclaimer"
            name="deposit_disclaimer"
            class="mt-3"
            v-model="form.deposit_disclaimer"
            :error="errors.deposit_disclaimer ? errors.deposit_disclaimer[0] : null"
            :maxlength=255
        />

        <input-text
            label="Fees Range"
            name="fees_range"
            class="mt-3"
            v-model="form.fees_range"
            :error="errors.fees_range ? errors.fees_range[0] : null"
            :maxlength=255
        />

        <currency-amount-list
            v-model="form.min_deposits"
            name="min_deposits"
            label="* Minimum deposit"
            :withoutLimits="true"
            :language="form.language"
            :errors="errors"
        />

        <currency-amount-list
            v-model="form.max_deposits"
            name="max_deposits"
            label="* Maximum deposit"
            :withoutLimits="true"
            :language="form.language"
            :errors="errors"
        />

        <currency-amount-list
            v-model="form.withdrawal_limits"
            name="withdrawal_limits"
            label="* Withdrawal limit"
            :withoutLimits="true"
            :language="form.language"
            :errors="errors"
        />

        <input-text
            label="Number of slots"
            name="number_of_slots"
            class="mt-3"
            v-model="form.number_of_slots"
            :error="errors.number_of_slots ? errors.number_of_slots[0] : null"
        />

        <input-text
            label="Number of blackjack games"
            name="number_of_blackjack_games"
            class="mt-3"
            v-model="form.number_of_blackjack_games"
            :error="errors.number_of_blackjack_games ? errors.number_of_blackjack_games[0] : null"
        />

        <input-text
            label="Number of roulette games"
            name="number_of_roulette_games"
            class="mt-3"
            v-model="form.number_of_roulette_games"
            :error="errors.number_of_roulette_games ? errors.number_of_roulette_games[0] : null"
        />

        <input-text
            label="Number of live games"
            name="number_of_live_games"
            class="mt-3"
            v-model="form.number_of_live_games"
            :error="errors.number_of_live_games ? errors.number_of_live_games[0] : null"
        />

        <input-text
            label="Number of total games"
            name="number_of_total_games"
            class="mt-3"
            v-model="form.number_of_total_games"
            :error="errors.number_of_total_games ? errors.number_of_total_games[0] : null"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Most Popular Game Types</p>
            <multi-reference
                create-text="Create new game type and link game type to this page"
                link-text="Link existing most popular game types"
                :creatable="false"
                :language="form.language"
                v-model="form.most_popular_games"
                :should-open-new-window="true"
                model-data="Game Type"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>

        <input-text
            label="Security Rating (score from 0 to 5 including decimals)"
            name="security_rating"
            class="mt-3"
            v-model="form.security_rating"
            :error="errors.security_rating ? errors.security_rating[0] : null"
        />

        <Select
            label="Security"
            name="security"
            v-model="form.security"
            class="mt-2"
            :options="[
                {value: 1, name: 'Very Poor'},
                {value: 2, name: 'Poor'},
                {value: 3, name: 'Fair'},
                {value: 4, name: 'Good'},
                {value: 5, name: 'Excellent'},
            ]"
            :error="errors.security ? errors.security[0] : null"
        />

        <Select
            label="Customer Service"
            name="customer_service"
            v-model="form.customer_service"
            class="mt-2"
            :options="[
                {value: 1, name: 'Very Poor'},
                {value: 2, name: 'Poor'},
                {value: 3, name: 'Fair'},
                {value: 4, name: 'Good'},
                {value: 5, name: 'Excellent'},
            ]"
            :error="errors.customer_service ? errors.customer_service[0] : null"
        />

        <input-text
            label="Affiliate Programs"
            name="affiliate_program"
            class="mt-3"
            v-model="form.affiliate_program"
            :error="errors.affiliate_program ? errors.affiliate_program[0] : null"
            :maxlength=255
        />
        <input-text
            label="Stock"
            name="stock"
            class="mt-3"
            v-model="form.stock"
            :error="errors.stock ? errors.stock[0] : null"
            :maxlength=255
        />
        <input-text
            label="Key People"
            name="key_people"
            class="mt-3"
            v-model="form.key_people"
            :error="errors.key_people ? errors.key_people[0] : null"
            :maxlength=255
        />
        <input-text
            label="Brands owned"
            name="brands_owned"
            class="mt-3"
            v-model="form.brands_owned"
            :error="errors.brands_owned ? errors.brands_owned[0] : null"
            :maxlength=255
        />
        <input-text
            label="Encryption"
            name="encryption"
            class="mt-3"
            v-model="form.encryption"
            :error="errors.encryption ? errors.encryption[0] : null"
            :maxlength=255
        />
        <input-text
            label="Seal of approval"
            name="seal_of_approval"
            class="mt-3"
            v-model="form.seal_of_approval"
            :error="errors.seal_of_approval ? errors.seal_of_approval[0] : null"
            :maxlength=255
        />
        <input-text
            label="Problem gambling organisations"
            name="problem_gambling_organisations"
            class="mt-3"
            v-model="form.problem_gambling_organisations"
            :error="errors.problem_gambling_organisations ? errors.problem_gambling_organisations[0] : null"
            :maxlength=255
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Average RTP of all games</p>
            <reference
                create-text="Create new RTP and link RTP to this page"
                link-text="Link an existing RTP"
                :creatable="false"
                :language="form.language"
                v-model="form.payout_ratio"
                model-data="Payout Ratio"
            />
        </div>

        <input-text
            label="No. of Bonuses"
            name="no_of_bonuses"
            class="mt-3"
            v-model="form.no_of_bonuses"
            :error="errors.no_of_bonuses ? errors.no_of_bonuses[0] : null"
            :maxlength=255
        />

        <input-text
            label="Total value of all bonuses"
            name="bonus_value"
            class="mt-3"
            v-model="form.bonus_value"
            :error="errors.bonus_value ? errors.bonus_value[0] : null"
            :maxlength=255
        />

        <input-text
            class="mt-3"
            name="usp_1"
            label="USP 1"
            v-model="form.usp_1"
            :error="errors.usp_1 ? errors.usp_1[0] : null"
            :maxlength=255
        />
        <media-reference
            v-model="form.usp_one_icon"
            class="mt-3"
            label="USP 1 Icon"
            :media-types="['Icon']"
        />

        <input-text
            class="mt-3"
            name="usp_2"
            label="USP 2"
            v-model="form.usp_2"
            :error="errors.usp_2 ? errors.usp_2[0] : null"
            :maxlength=255
        />
        <media-reference
            v-model="form.usp_two_icon"
            class="mt-3"
            label="USP 2 Icon"
            :media-types="['Icon']"
        />

        <input-text
            class="mt-3"
            name="usp_3"
            label="USP 3"
            v-model="form.usp_3"
            :error="errors.usp_3 ? errors.usp_3[0] : null"
            :maxlength=255
        />
        <media-reference
            v-model="form.usp_three_icon"
            class="mt-3"
            label="USP 3 Icon"
            :media-types="['Icon']"
        />

        <input-checkbox
            name="exclude_from_rankings"
            label="Exclude from rankings / bonus lists?"
            class="mt-3"
            v-model="form.exclude_from_rankings"
            :error="errors.exclude_from_rankings ? errors.exclude_from_rankings[0] : null"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Casino Usage in Rankings and Bonus Lists</p>

            <casino-ranking-usage
                v-if="data && data.id"
                :casino-id="data.id"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import Select from "./form/Select";
import moment from "moment";
import Translator from "./form/Translator";

export default {
    name: "CasinoForm",
    components: {Translator, Select},
    mixins: [crudForm],
    props: {
        languageData: {
            type: Object,
            required: true,
        },
        linkPaymentMethods: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                name: this.getFormData("name"),
                launch_date: this.getFormData("launch_date"),
                is_out_of_business: this.getFormData("is_out_of_business", 0),
                out_of_business_date: this.getFormData("out_of_business_date"),
                is_not_recommended: !!this.getFormData("is_not_recommended", false),
                mobile_site_experience: this.getFormData("mobile_site_experience"),
                available_as_mobile_app: this.getFormData("available_as_mobile_app"),
                has_mobile_bonuses: this.getFormData("has_mobile_bonuses"),
                has_live_chat: this.getFormData("has_live_chat"),
                is_compliant: this.getFormData("is_compliant"),
                notes: this.getFormData("notes"),
                gameTypes: this.getReferenceData("game_types"),
                depositMethods: this.getReferenceData("deposit_methods"),
                withdrawalMethods: this.getReferenceData("withdrawal_methods"),
                targetMarkets: this.getReferenceData("target_markets") ?? [],
                restrictedCountries: this.getReferenceData("restricted_countries") ?? [],
                licenses: this.getReferenceData("licenses"),
                casino: this.casinoData ? JSON.parse(this.casinoData) : this.casinoData,
                currencies: this.getFormData("currencies"),
                casinoLanguages: this.getReferenceData("casino_languages"),
                trust_checkers: this.getFormData("trust_checkers"),
                software: this.getFormData("software"),
                most_popular_games: this.getFormData("most_popular_games"),
                devices: this.getReferenceData("devices"),
                fees_range: this.getReferenceData("fees_range"),
                photo_id: this.getFormData("photo_id"),
                photo: this.getFormData("photo"),
                company_behind_id: this.getFormData("company_behind_id"),
                company_behind: this.getFormData("company_behind"),
                payout_within_mins: this.getFormData("payout_within_mins"),
                payout_disclaimer: this.getFormData("payout_disclaimer"),
                deposit_time: this.getFormData("deposit_time"),
                deposit_disclaimer: this.getFormData("deposit_disclaimer"),
                number_of_slots: this.getFormData("number_of_slots"),
                number_of_blackjack_games: this.getFormData("number_of_blackjack_games"),
                no_of_bonuses: this.getFormData("no_of_bonuses"),
                number_of_roulette_games: this.getFormData("number_of_roulette_games"),
                number_of_live_games: this.getFormData("number_of_live_games"),
                number_of_total_games: this.getFormData("number_of_total_games"),
                security: this.getFormData("security"),
                customer_service: this.getFormData("customer_service"),
                security_rating: this.getFormData("security_rating"),
                affiliate_program: this.getFormData("affiliate_program"),
                stock: this.getFormData("stock"),
                key_people: this.getFormData("key_people"),
                brands_owned: this.getFormData("brands_owned"),
                encryption: this.getFormData("encryption"),
                seal_of_approval: this.getFormData("seal_of_approval"),
                problem_gambling_organisations: this.getFormData("problem_gambling_organisations"),
                payout_ratio_id: this.getFormData("payout_ratio_id"),
                payout_ratio: this.getFormData("payout_ratio"),
                bonus_value: this.getFormData("bonus_value"),
                min_deposits: this.getFormData("min_deposits", []),
                max_deposits: this.getFormData("max_deposits", []),
                withdrawal_limits: this.getFormData("withdrawal_limits", []),
                usp_1: this.getFormData("usp_1"),
                usp_2: this.getFormData("usp_2"),
                usp_3: this.getFormData("usp_3"),
                usp_1_icon_id: this.getFormData("usp_1_icon_id"),
                usp_one_icon: this.getFormData("usp_one_icon"),
                usp_2_icon_id: this.getFormData("usp_2_icon_id"),
                usp_two_icon: this.getFormData("usp_two_icon"),
                usp_3_icon_id: this.getFormData("usp_3_icon_id"),
                usp_three_icon: this.getFormData("usp_three_icon"),
                exclude_from_rankings: !!this.getFormData("exclude_from_rankings", false),
                bonuses: this.getFormData("bonuses"),
            },
        };
    },
    watch: {
        "form.photo": function(newValue){
            this.form.photo_id = newValue ? newValue.id : null;
        },
        "form.payout_ratio": function(newValue){
            this.form.payout_ratio_id = newValue ? newValue.id : null;
        },
        "form.company_behind": function (newValue) {
            this.form.company_behind_id = newValue ? newValue.id : null;
        },
        "form.usp_one_icon": function (newValue) {
            this.form.usp_1_icon_id = newValue ? newValue.id : null;
        },
        "form.usp_two_icon": function (newValue) {
            this.form.usp_2_icon_id = newValue ? newValue.id : null;
        },
        "form.usp_three_icon": function (newValue) {
            this.form.usp_3_icon_id = newValue ? newValue.id : null;
        },
        "form.depositMethods": function (newValue) {
            if (this.linkPaymentMethods) {
                this.form.withdrawalMethods = [...newValue];
            }
        },
        "form.withdrawalMethods": function (newValue) {
            // withdrawalMethods list doesn't match depositMethods, means withdrawalMethods was user-edited, we stop linking them now
            if (this.linkPaymentMethods && JSON.stringify(this.form.depositMethods) !== JSON.stringify(newValue)) {
                this.linkPaymentMethods = false;
            }
        }
    },
    computed: {
        bonusHighlight(){
            return this.form.bonuses.filter(bonus => {
                // return if expiry date less than today
                // return if expiry date within next 7 days
                return moment(bonus.expiry_date).isBefore(moment()) ||
                    moment(bonus.expiry_date).isBetween(moment(), moment().add(7, "days"));
            }).map(bonus => {
                return {
                    id: bonus.id,
                    title: "This bonus has or will expire soon"
                }
            });
        }
    }
};
</script>
